import React, { createContext, FC, useState, useContext, useMemo, useEffect, useRef } from 'react';
import { nearestChimeRegionApi } from 'src/api/nearestChimeRegionAPI';
import logger from 'src/lib/logger';

export interface ChimeRegion {
  region: string;
}

export const ChimeRegionContext = createContext<ChimeRegion | null>(null);

/**
 * The ChimeRegionProvider is used to provide the nearest chime
 * region to connect calls through
 */
const ChimeRegionProvider: FC<WithChildren> = ({ children }) => {
  const [region, setRegion] = useState<string>('');
  const isProviderMounted = useRef<boolean>(true);

  useEffect(() => {
    if (!region) {
      nearestChimeRegionApi().then(({ region, errorMessage }) => {
        // In the event that an error is caught by the upper ErrorBoundary, it will unmount all of the happy path
        // components downstream including this provider.  Keeping track of this component's mounted state
        // and only calling setRegion when its mounted will prevent an error from being thrown.
        if (isProviderMounted.current) {
          setRegion(region);
          if (errorMessage !== '') {
            logger.error(`App: ${errorMessage}`);
          }
        }
      });
    }
    return () => {
      isProviderMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      region,
    }),
    [region]
  );

  return <ChimeRegionContext.Provider value={value}>{children}</ChimeRegionContext.Provider>;
};

/**
 * useChimeRegion should be used within a child component of
 * ChimeRegionProvider. It gives you access to the nearest chime
 * region.
 */
function useChimeRegionState(): ChimeRegion {
  const state = useContext(ChimeRegionContext);

  if (!state) {
    throw new Error('useChimeRegionState must be used within a ChimeRegionProvider');
  }

  return state;
}

export { useChimeRegionState };

export default ChimeRegionProvider;
